import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Loader2, FileText, Download, Smartphone, Globe, Eye, Bot } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const METHOD_COLORS = {
    GET: 'bg-sky-500/20 text-sky-300 border-sky-700',
    POST: 'bg-emerald-500/20 text-emerald-300 border-emerald-700',
    PUT: 'bg-amber-500/20 text-amber-300 border-amber-700',
    PATCH: 'bg-orange-500/20 text-orange-300 border-orange-700',
    DELETE: 'bg-red-500/20 text-red-300 border-red-700',
};

const CATEGORIES = [
    { key: 'mobile', label: 'Mobile API', icon: Smartphone, description: 'Endpoints untuk aplikasi mobile (require X-Mobile-Token + JWT).' },
    { key: 'third_party', label: 'Third-Party API', icon: Globe, description: 'Endpoints untuk partner integrasi eksternal.' },
    { key: 'openclaw', label: 'OpenClaw', icon: Bot, description: 'Kontrak HTTP yang DigiTask harapkan dari server OpenClaw eksternal (untuk vendor/operator OpenClaw).' },
];

export default function ApiDocsManager() {
    const [activeTab, setActiveTab] = useState('mobile');
    const [preview, setPreview] = useState({});
    const [loading, setLoading] = useState({});
    const [downloading, setDownloading] = useState({});

    const getToken = () => localStorage.getItem('superadmin_token');
    const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

    const loadPreview = useCallback(async (cat) => {
        setLoading((s) => ({ ...s, [cat]: true }));
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/api-docs/preview`, {
                params: { category: cat },
                headers: authHeaders(),
            });
            setPreview((p) => ({ ...p, [cat]: res.data }));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal memuat preview');
        } finally {
            setLoading((s) => ({ ...s, [cat]: false }));
        }
    }, []);

    useEffect(() => {
        if (!preview[activeTab]) loadPreview(activeTab);
    }, [activeTab, preview, loadPreview]);

    const downloadPdf = async (cat) => {
        setDownloading((s) => ({ ...s, [cat]: true }));
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/api-docs/pdf`, {
                params: { category: cat },
                headers: authHeaders(),
                responseType: 'blob',
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `digitask-${cat.replace('_', '-')}-api.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF berhasil diunduh');
        } catch (e) {
            toast.error('Gagal mengunduh PDF');
        } finally {
            setDownloading((s) => ({ ...s, [cat]: false }));
        }
    };

    const openPdfPreview = (cat) => {
        const token = getToken();
        // Open PDF inline with bearer auth via a blob fetch
        axios.get(`${API_BASE}/api/superadmin/api-docs/pdf`, {
            params: { category: cat },
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
        }).then((res) => {
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
        }).catch(() => toast.error('Gagal membuka preview'));
    };

    return (
        <div className="space-y-6" data-testid="api-docs-manager">
            <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <FileText className="w-6 h-6 text-amber-500" /> API Documentation
                </h1>
                <p className="text-slate-400 mt-1">Generate & unduh dokumentasi API terstruktur dalam format PDF.</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-slate-800 border border-slate-700">
                    {CATEGORIES.map((c) => (
                        <TabsTrigger key={c.key} value={c.key} className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-slate-300" data-testid={`api-docs-tab-${c.key}`}>
                            <c.icon className="w-4 h-4 mr-2" /> {c.label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {CATEGORIES.map((c) => (
                    <TabsContent key={c.key} value={c.key} className="space-y-5 mt-4">
                        <Card className="bg-slate-800 border-slate-700">
                            <CardHeader className="flex flex-row items-start justify-between">
                                <div>
                                    <CardTitle className="text-white flex items-center gap-2">
                                        <c.icon className="w-5 h-5 text-amber-400" />
                                        {c.label}
                                        {preview[c.key]?.count != null && (
                                            <Badge variant="secondary" className="ml-2">{preview[c.key].count} endpoints</Badge>
                                        )}
                                    </CardTitle>
                                    <CardDescription className="text-slate-400 mt-1">{c.description}</CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => openPdfPreview(c.key)}
                                        className="bg-slate-900 border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white"
                                        data-testid={`api-docs-preview-pdf-${c.key}`}
                                    >
                                        <Eye className="w-4 h-4 mr-2" /> Preview PDF
                                    </Button>
                                    <Button
                                        onClick={() => downloadPdf(c.key)}
                                        disabled={downloading[c.key]}
                                        className="bg-amber-500 hover:bg-amber-600 text-white"
                                        data-testid={`api-docs-download-${c.key}`}
                                    >
                                        {downloading[c.key] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                                        Download PDF
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loading[c.key] ? (
                                    <div className="flex items-center justify-center py-10">
                                        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                                    </div>
                                ) : !preview[c.key] ? (
                                    <div className="text-slate-500 text-sm">Klik tab untuk memuat preview.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-slate-700 text-slate-400">
                                                    <th className="text-left py-2 px-2 w-20">Method</th>
                                                    <th className="text-left py-2 px-2">Path</th>
                                                    <th className="text-left py-2 px-2">Summary</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {preview[c.key].endpoints.map((e, i) => (
                                                    <tr key={i} className="border-b border-slate-800 hover:bg-slate-900/40" data-testid={`api-docs-row-${c.key}-${i}`}>
                                                        <td className="py-2 px-2">
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${METHOD_COLORS[e.method] || 'bg-slate-700 text-slate-300'}`}>
                                                                {e.method}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-2 font-mono text-xs text-slate-200">{e.path}</td>
                                                        <td className="py-2 px-2 text-slate-300">{e.summary || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
